import React, { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { formatDistanceToNow } from "date-fns";
import api from "../../../../chat/api";
import EditorPic from "../Proposal Section View/Editor.png";
import RemoveMemberPopover from "../Proposal Section View/RemoveMemberPopover";
import {
  ExpandCollapseIcon,
  ApproveIcon,
  ChangesIcon,
  PendingIcon,
  ReviewIcon,
  ButtonCheckIcon,
  ResetIcon,
  RedCrossIcon
} from "../Proposal Section View/svgIcons";

const ReviewStatusTab = ({
  isOpen, 
  proposalData,
  filterType,
  expandedMemberIds,
  toggleMemberCard,
  getInitials,
  orgId,
  folderId,
  onRefresh,
  userPermission,
  currentUserEmail,
}) => {
  const [reminderStatuses, setReminderStatuses] = useState({});
  const [markDoneStatuses, setMarkDoneStatuses] = useState({});
  const [resetStatuses, setResetStatuses] = useState({});
  const [reviewerActionStatuses, setReviewerActionStatuses] = useState({});

  const [requestingChangesUserId, setRequestingChangesUserId] = useState(null); 
  const [changeRequestText, setChangeRequestText] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setReminderStatuses({});
      setMarkDoneStatuses({});
      setResetStatuses({});
      setReviewerActionStatuses({});
      setRequestingChangesUserId(null);
      setChangeRequestText("");
    }
  }, [isOpen]);

  const aggregatedReviews = useMemo(() => {
    const sections = proposalData?.sections || proposalData?.analysis?.toc || proposalData?.proposal_content || [];

    let list = [];

    sections.forEach((section) => {
      const members = section.assigned_members || [];
      members.forEach((member) => {
        if (userPermission !== "admin" && userPermission !== "owner" && userPermission) {
          if (member.email !== currentUserEmail) return;
        }
        if (filterType !== "All" && member.role?.toLowerCase() !== filterType.toLowerCase()) return;

        let latestTs = 0;
        if (member.review_status?.change_requests?.length > 0) {
          const timestamps = member.review_status.change_requests.map((cr) => new Date(cr.created_at || 0).getTime());
          latestTs = Math.max(...timestamps);
        }

        const sectionId = section.id || section.section_id;

        list.push({
          ...member,
          sectionNumber: section.number,
          sectionTitle: section.title,
          sectionId: sectionId,
          latestTs,
          uniqueId: `review-${member.user_id}-${sectionId}`,
        });
      });
    });

    return list.sort((a, b) => b.latestTs - a.latestTs);
  }, [proposalData, userPermission, currentUserEmail, filterType]);

  const handleSendReminder = async (memberUserId, sectionId, uniqueId) => {
    if (!orgId || !folderId || !sectionId) return toast.error("Missing info");
    setReminderStatuses((prev) => ({ ...prev, [uniqueId]: "sending" }));

    try {
      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/send-reminder/`, {
        org_id: parseInt(orgId),
        section_id: sectionId,
        user_id: memberUserId,
      });
      setReminderStatuses((prev) => ({ ...prev, [uniqueId]: "success" }));
      toast.success("Reminder sent successfully");
      setTimeout(() => setReminderStatuses((prev) => ({ ...prev, [uniqueId]: "disabled" })), 2000);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to send reminder");
      setReminderStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
    }
  };

  const handleMarkAsDone = async (sectionId, uniqueId) => {
    if (!orgId || !folderId || !sectionId) return;
    setMarkDoneStatuses((prev) => ({ ...prev, [uniqueId]: "loading" }));

    try {
      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/editor-mark-done/`, {
        org_id: parseInt(orgId),
        section_id: sectionId,
        done: true,
      });
      setMarkDoneStatuses((prev) => ({ ...prev, [uniqueId]: "success" }));
      toast.success("Section marked as done");
      setTimeout(() => {
        setMarkDoneStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
        if (onRefresh) onRefresh();
      }, 2000);
    } catch (error) {
      toast.error("Failed to update status");
      setMarkDoneStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
    }
  };

  const handleResetStatus = async (memberUserId, sectionId, uniqueId) => {
    if (!orgId || !folderId || !sectionId) return;
    setResetStatuses((prev) => ({ ...prev, [uniqueId]: true }));

    try {
      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/reset-status/`, {
        org_id: parseInt(orgId),
        section_id: sectionId,
        user_id: memberUserId,
      });
      toast.success("Review status reset");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to reset status");
    } finally {
      setResetStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
    }
  };

  const handleRemoveMember = async (memberUserId, sectionId) => {
    if (!orgId || !folderId || !sectionId) throw new Error("Missing info");
    await api.post(`/api/v1/rfp/folders/${folderId}/section-view/unassign-member/`, {
      org_id: parseInt(orgId),
      section_id: sectionId,
      user_id: memberUserId,
    });
  };

  const handleApprove = async (sectionId, uniqueId) => {
    if (!orgId || !folderId || !sectionId) return;
    setReviewerActionStatuses((prev) => ({ ...prev, [uniqueId]: "loading-approve" }));

    try {
      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/reviewer-approve/`, {
        org_id: parseInt(orgId),
        section_id: sectionId,
        approved: true,
      });
      setReviewerActionStatuses((prev) => ({ ...prev, [uniqueId]: "success-approve" }));
      toast.success("Section approved");
      setTimeout(() => {
        setReviewerActionStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
        if (onRefresh) onRefresh();
      }, 2000);
    } catch (error) {
      toast.error("Failed to approve section");
      setReviewerActionStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
    }
  };

  const handleStartRequestChanges = (uniqueId) => {
    setRequestingChangesUserId(uniqueId);
    setChangeRequestText("");
  };

  const handleCancelRequestChanges = () => {
    setRequestingChangesUserId(null);
    setChangeRequestText("");
  };

  const handleSubmitRequestChanges = async (sectionId, uniqueId) => {
    if (!changeRequestText.trim()) return toast.error("Please enter a reason for changes");
    setReviewerActionStatuses((prev) => ({ ...prev, [uniqueId]: "loading-changes" }));

    try {
      await api.post(`/api/v1/rfp/folders/${folderId}/section-view/reviewer-request-change/`, {
        org_id: parseInt(orgId),
        section_id: sectionId,
        change_request: changeRequestText,
      });
      setReviewerActionStatuses((prev) => ({ ...prev, [uniqueId]: "success-changes" }));
      toast.success("Change request saved");
      setTimeout(() => {
        setRequestingChangesUserId(null);
        setChangeRequestText("");
        setReviewerActionStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
        if (onRefresh) onRefresh();
      }, 1500);
    } catch (error) {
      toast.error("Failed to save change request");
      setReviewerActionStatuses((prev) => { const newState = { ...prev }; delete newState[uniqueId]; return newState; });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
      case "approved":
      case "completed": return <ApproveIcon />;
      case "changes_requested": return <RedCrossIcon />;
      case "pending":
      default: return <PendingIcon />;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-3 py-6 pt-0 custom-scrollbar">
      {aggregatedReviews.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="mb-6 relative">
            <img src={EditorPic} alt="No team members" className="w-48 h-auto object-contain" />
          </div>
          <p className="text-[#6B7280] font-outfit text-base font-medium text-center mb-1">
            No team members assigned yet!
          </p>
        </div>
      )}

      {aggregatedReviews.length > 0 && (
        <div className="space-y-4">
          {aggregatedReviews.map((item, idx) => {
            const isExpanded = expandedMemberIds.has(item.uniqueId);
            const reviewStatus = (item.review_status?.status || "pending").toLowerCase();
            const isDone = reviewStatus === "done" || reviewStatus === "completed" || reviewStatus === "approved";
            const isPending = reviewStatus === "pending";
            const isChangesRequested = reviewStatus === "changes_requested";

            const reminderStatus = reminderStatuses[item.uniqueId];
            const isSendingReminder = reminderStatus === "sending";
            const isReminderSuccess = reminderStatus === "success";
            const isReminderDisabled = reminderStatus === "disabled";

            const doneStatus = markDoneStatuses[item.uniqueId];
            const isMarking = doneStatus === "loading";
            const isMarkedSuccess = doneStatus === "success";

            const isResetting = resetStatuses[item.uniqueId];

            const reviewerAction = reviewerActionStatuses[item.uniqueId];
            const isApproving = reviewerAction === "loading-approve";
            const isApprovedSuccess = reviewerAction === "success-approve";
            const isSavingChanges = reviewerAction === "loading-changes";
            const isSavedChangesSuccess = reviewerAction === "success-changes";

            const isRequestingChangesMode = requestingChangesUserId === item.uniqueId;

            const isReviewer = userPermission === "reviewer";
            const isEditor = userPermission === "editor";
            const isAdminOrOwner = userPermission === "admin" || userPermission === "owner";

            const showEditorMarkDone = isEditor && !isDone;
            const showReviewerActions = isReviewer && !isDone && !isChangesRequested && !isRequestingChangesMode;
            const showAdminControls = isAdminOrOwner || (!isReviewer && !isEditor);

            const latestChangeRequest =
              item.review_status?.change_requests && item.review_status.change_requests.length > 0
                ? item.review_status.change_requests[item.review_status.change_requests.length - 1]
                : null;

            return (
              <div key={item.uniqueId || idx} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                <div
                  className={`p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isExpanded ? "rounded-t-lg" : "rounded-lg"
                  }`}
                  onClick={() => toggleMemberCard(item.uniqueId)}
                >
                  <div className="w-10 h-10 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit font-medium flex-shrink-0">
                    {getInitials(item.full_name || item.email)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[#18101C] font-outfit text-[14px] font-medium truncate">
                        {item.full_name || item.email}
                      </p>
                      <span className="bg-[#FFF2FE] text-[#C95EBE] text-[10px] px-2 py-0.5 rounded-[8px] font-medium">
                        {item.role || "Editor"}
                      </span>
                    </div>
                    <p className="text-[#3F3F46] font-outfit text-[12px]">
                      {item.sectionNumber}. {item.sectionTitle}
                    </p>
                  </div>

                  <ExpandCollapseIcon className={`transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 rounded-b-lg">
                    <div className="h-px w-full bg-gray-100 mb-4"></div>

                    <div className="mb-4">
                      <label className="text-[#3F3F46] font-outfit text-[14px] font-medium mb-2 block">
                        Section Assigned
                      </label>

                      <div className="space-y-3">
                        <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-[#374151] font-outfit text-[14px] font-medium flex-1">
                              {item.sectionNumber}. {item.sectionTitle}
                            </p>
                            <div className="flex-shrink-0 ml-2">
                              {getStatusIcon(reviewStatus)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {isChangesRequested && latestChangeRequest && (
                        <div className="mt-3 bg-white border border-[#EAB308] rounded-lg p-4">
                          <h4 className="text-[#EAB308] font-outfit text-[14px] font-semibold mb-2">
                            Changes Requested
                          </h4>
                          <p className="text-[#374151] font-outfit text-[14px] leading-relaxed mb-3">
                            {latestChangeRequest.comment}
                          </p>
                          {latestChangeRequest.created_at && (
                            <p className="text-[#6B7280] font-outfit text-xs">
                              {formatDistanceToNow(new Date(latestChangeRequest.created_at), { addSuffix: true })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {isRequestingChangesMode && (
                      <div className="mt-2 mb-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        <label className="block text-[#3F3F46] font-outfit text-[14px] font-medium mb-2">
                          Reason for Changes
                        </label>
                        <textarea
                          value={changeRequestText}
                          onChange={(e) => setChangeRequestText(e.target.value)}
                          disabled={isSavingChanges || isSavedChangesSuccess}
                          className="w-full border border-gray-300 rounded-lg p-3 text-[14px] font-outfit focus:outline-none focus:ring-1 focus:ring-[#C95EBE] focus:border-[#C95EBE] resize-none mb-3 disabled:bg-gray-50 disabled:text-gray-500"
                          rows="4"
                          placeholder="Describe what changes are needed..."
                        />
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={handleCancelRequestChanges}
                            disabled={isSavingChanges || isSavedChangesSuccess}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-[#374151] text-[14px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSubmitRequestChanges(item.sectionId, item.uniqueId)}
                            disabled={isSavingChanges || isSavedChangesSuccess}
                            className="px-6 py-2 bg-[#C95EBE] rounded-lg text-white text-[14px] font-medium hover:bg-[#B845AD] transition-colors disabled:opacity-70 min-w-[100px]"
                          >
                            {isSavingChanges ? "Saving..." : isSavedChangesSuccess ? "Saved" : "Submit"}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {showEditorMarkDone && (
                        <button
                          onClick={() => handleMarkAsDone(item.sectionId, item.uniqueId)}
                          disabled={isMarking || isMarkedSuccess}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all bg-[#22C55E] hover:bg-[#16A34A] text-white disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
                        >
                          {isMarking ? "Hang on..." : isMarkedSuccess ? "Done" : <><ButtonCheckIcon className="w-5 h-5" /> Mark as Done</>}
                        </button>
                      )}

                      {showReviewerActions && (
                        <div className="flex items-center gap-3 w-full">
                          <button
                            onClick={() => handleStartRequestChanges(item.uniqueId)}
                            disabled={isApproving || isApprovedSuccess}
                            className="flex-1 px-4 py-2.5 rounded-lg text-[14px] font-medium border border-[#E11D48] text-[#E11D48] bg-white hover:bg-[#FFF1F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Request Changes
                          </button>

                          <button
                            onClick={() => handleApprove(item.sectionId, item.uniqueId)}
                            disabled={isApproving || isApprovedSuccess}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[14px] font-medium bg-[#22C55E] hover:bg-[#16A34A] text-white transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                          >
                            {isApproving ? "Approving..." : isApprovedSuccess ? "Approved" : <><ButtonCheckIcon className="w-5 h-5" /> Approve</>}
                          </button>
                        </div>
                      )}

                      {showAdminControls && (
                        <>
                          <RemoveMemberPopover
                            onConfirm={() => handleRemoveMember(item.user_id, item.sectionId)}
                            onAfterSuccess={onRefresh}
                            buttonClassName="flex-1 w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-[#374151] text-[14px] font-medium hover:bg-gray-50 transition-colors"
                          />

                          {isDone || isChangesRequested ? (
                            <button
                              onClick={() => handleResetStatus(item.user_id, item.sectionId, item.uniqueId)}
                              disabled={isResetting}
                              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 bg-white rounded-lg text-[14px] font-medium text-[#6B7280] hover:bg-gray-50 transition-colors flex-1"
                            >
                              <ResetIcon className={`w-4 h-4 ${isResetting ? "animate-spin" : ""}`} />
                              {isResetting ? "Resetting..." : "Reset Status"}
                            </button>
                          ) : isPending ? (
                            <button
                              onClick={() => handleSendReminder(item.user_id, item.sectionId, item.uniqueId)}
                              disabled={isSendingReminder || isReminderSuccess || isReminderDisabled}
                              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-[14px] font-medium transition-colors flex-1 ${
                                isReminderSuccess
                                  ? "bg-green-50 border-green-200 text-green-700 cursor-default"
                                  : isSendingReminder || isReminderDisabled
                                  ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                                  : "bg-white border-gray-200 text-[#374151] hover:bg-gray-50"
                              }`}
                            >
                              {isSendingReminder ? "Sending..." : isReminderSuccess ? "Sent" : <><ReviewIcon /> Send Reminder</>}
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewStatusTab;
